import * as React from 'react';
import { Button } from '@vechaiui/react';
import { Form, Formik, FormikProps } from 'formik';
import { useLocalization } from '@fluent/react';
import { useMutation } from '@apollo/client';
import { useRef, useState } from 'react';
import { useToggle } from '@react-hookz/web';

import Dialog from '../controls/Dialog';
import OrderCancellationForm from '../forms/OrderCancellationForm';
import yup from '#/libs/yup';
import { CancelOrderDocument } from '#/__generated__/graphql/graphql';
import { ORDER_CANCELLATION_INITIAL_VALUES } from '#/constants/form-initial-values';
import { orderCancellationSchema } from '#/constants/validation-schema';
import { sanitizeOrderCancellationFormData } from '#/utils/sanitize-data';
import { useManagingProfile } from '#/hooks';

const OrderCancellationPanel: React.FC<OrderCancellationPanelProps> = ({
  orderId,
  orderNumber,
}) => {
  const { l10n } = useLocalization();
  const [cancelOrderMutation] = useMutation(CancelOrderDocument);
  const formRef = useRef<FormikProps<any>>(null);
  const managingProfile = useManagingProfile();

  const [apiErrors, setApiErrors] = useState<{ message: string }[]>();

  const [isCancelOrderDialogOpen, toggleCancelOrderDialog] = useToggle(false);

  const handleClose = () => {
    toggleCancelOrderDialog(false);
    formRef.current?.resetForm();
    window.location.reload();
  };

  return (
    <div>
      <div className="flex justify-center">
        <Formik
          innerRef={formRef}
          initialValues={ORDER_CANCELLATION_INITIAL_VALUES}
          onSubmit={async (formData, { resetForm }) => {
            const form = sanitizeOrderCancellationFormData(formData, managingProfile!.__typename);
            try {
              await cancelOrderMutation({
                variables: { input: { id: orderId, remark: form.remark } },
              });
              toggleCancelOrderDialog(true);
            } catch (err: any) {
              if (err.errors) {
                setApiErrors(err.errors);
              } else if (err.networkError?.result.errors) {
                setApiErrors(err.networkError.result.errors);
              } else {
                setApiErrors([err]);
              }
            }
          }}
          validationSchema={yup.object(orderCancellationSchema)}
        >
          <Form className="flex flex-col justify-between w-96 min-h-[16rem]">
            <div>
              <OrderCancellationForm userType={managingProfile!.__typename} />
            </div>

            <footer className="mt-12 space-y-4">
              <Button
                className="w-full rounded-full"
                color="primary"
                size="xl"
                type="submit"
                variant="solid"
              >
                {l10n.getString('cancel-order')}
              </Button>
            </footer>
          </Form>
        </Formik>
      </div>
      <Dialog
        isOpen={isCancelOrderDialogOpen}
        closeButton={l10n.getString('close')}
        onClose={() => handleClose()}
        title={`${l10n.getString('cancel-order')} #${orderNumber}`}
      >
        <div className="py-3 border-t border-black">
          <p className="pb-5">{l10n.getString('cancel-order-success')}</p>
        </div>
      </Dialog>
    </div>
  );
};
export type OrderCancellationPanelProps = {
  orderId: string;
  orderNumber: string;
};

export default OrderCancellationPanel;
